import type { GeoTIFFImage } from 'ol/source/GeoTIFF';
import { fromBlob } from 'geotiff';
import { quantileBuckets, equalIntervalBuckets } from 'geobuckets/dist/src';
import type { ColorMap, GeoTiffMapLayer } from '../models';
import { ColorMapInterpolation, ColorMapMode } from '../enums';

export const getGeoTiffImage = async (blob: Blob) => {
  const tiff = await fromBlob(blob);

  return await tiff.getImage();
};

export const getGeoTiffValues = async (image: GeoTIFFImage, band: number, noDataValue: number) => {
  const [data] = await image.readRasters({
    samples: [band - 1],
    resampleMethod: 'bilinear'
  });

  const geoTiffNoDataValue = !noDataValue ? getGeoTiffNoDataValue(image) : noDataValue;

  return (data as any as number[]).filter((value) => value != geoTiffNoDataValue);
};

export const getGeoTiffNoDataValue = (image: GeoTIFFImage) => {
  return image.getGDALNoData() || 0;
};

export const defaultBandStyleFn = async (layer: GeoTiffMapLayer, values: number[]): Promise<any> => {
  const colorMap = layer.data.colorMap;
  const band = layer.data.band;
  const noDataValue = layer.data.noDataValue;

  if (colorMap) {
    const mode = colorMap.mode;

    switch (mode) {
      case ColorMapMode.QUANTILE:
        return await generateQuantileBandStyle(band, values, noDataValue, colorMap);
      default:
        return await generateEqualBandStyle(band, values, noDataValue, colorMap);
    }
  }

  return {
    color: ['case', ['==', ['band', band], noDataValue], '#00000000', '#ffffff']
  };
};

const generateQuantileBandStyle = async (
  band: number,
  values: number[],
  noDataValue: number,
  colorMap: ColorMap
): Promise<any> => {
  const { breaks, min, max } = await calculateGeoTiffColorInfo(values, colorMap, noDataValue);

  if (colorMap.interpolation === ColorMapInterpolation.DISCRETE) {
    breaks.shift();
    breaks.pop();

    return {
      color: generateDiscreteColors(band, colorMap.colors, breaks, noDataValue, min, max)
    };
  }

  return {
    color: generateLinearColors(band, colorMap.colors, breaks, noDataValue, min, max)
  };
};

const generateEqualBandStyle = async (
  band: number,
  values: number[],
  noDataValue: number,
  colorMap: ColorMap
): Promise<any> => {
  // TODO
  return await generateQuantileBandStyle(band, values, noDataValue, colorMap);
};

const getMaximumValue = async (values: number[]) => {
  let maximum = -Infinity;

  let index = values.length;
  while (index--) {
    maximum = values[index] > maximum ? values[index] : maximum;
  }
  return maximum;
};

const getMinimumValue = async (values: number[]) => {
  let minimum = Infinity;

  let index = values.length;
  while (index--) {
    minimum = values[index] < minimum ? values[index] : minimum;
  }
  return minimum;
};

export const calculateGeoTiffColorInfo = async (values: number[], colorMap: ColorMap, noDataValue = 0) => {
  const filteredValues = values.filter((value) => value != noDataValue);
  const min = await getMinimumValue(filteredValues);
  const max = await getMaximumValue(filteredValues);
  const colors = colorMap.colors;

  let breaks: number[] = [];
  const colorBreaks: { color: string; value: number; percent: number }[] = [];

  switch (colorMap.interpolation) {
    case ColorMapInterpolation.DISCRETE:
      breaks = await generateDiscreteColorBreaks(filteredValues, colors);
      for (let i = 0; i < breaks.length; i++) {
        colorBreaks.push({
          color: colors[i],
          value: breaks[i],
          percent: ((breaks[i] - min) / (max - min)) * 100
        });
      }

      break;
    case ColorMapInterpolation.LINEAR:
      {
        const linearBreaks = await generateLinearColorBreaks(filteredValues, colors, min, max);
        for (let i = 0; i < linearBreaks.length; i++) {
          colorBreaks.push({
            color: colors[i],
            value: linearBreaks[i],
            percent: ((linearBreaks[i] - min) / (max - min)) * 100
          });
        }

        // linear is binned using idx - idx+1 ranges
        // keep the first (lowest)
        // remove the second (first range end, second range start)
        // keep the rest (nth range end)
        for (let i = 0; i < linearBreaks.length; i++) {
          if (i === 1) {
            continue;
          }

          breaks.push(linearBreaks[i]);
        }
      }
      break;
    default:
      break;
  }

  return {
    min,
    max,
    breaks,
    colorBreaks
  };
};

export const generateDiscreteColorBreaks = async (values: number[], colors: string[]) => {
  return await quantileBuckets(values, colors.length);
};

export const generateLinearColorBreaks = async (values: number[], colors: string[], min: number, max: number) => {
  return await equalIntervalBuckets(values, colors.length, min, max);
};

const generateDiscreteColors = (
  band: number,
  colors: string[],
  breaks: number[],
  noDataValue: number,
  min: number,
  max: number
) => {
  const result = ['case', ['<', ['band', band], min], '#00000000'];

  result.push(['==', ['band', band], noDataValue]);
  result.push('#00000000');

  result.push(['>', ['band', band], max]);
  result.push('#00000000');

  breaks.forEach((value, i) => {
    result.push(['<=', ['band', band], value]);
    result.push(colors[i]);
  });

  result.push(colors[colors.length - 1]);

  return result;
};

const generateLinearColors = (
  band: number,
  colors: string[],
  breaks: number[],
  noDataValue: number,
  min: number,
  max: number
) => {
  const result = ['case', ['<', ['band', band], min], '#00000000'];
  const interpolate = ['interpolate', ['linear'], ['band', band]];

  result.push(['==', ['band', band], noDataValue]);
  result.push('#00000000');

  result.push(['>', ['band', band], max]);
  result.push('#00000000');

  breaks.forEach((value: any, i: number) => {
    if (i < colors.length) {
      interpolate.push(value);
      interpolate.push(colors[i]);
    }
  });

  result.push(interpolate);

  return result;
};
